import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  IconButton,
  Button,
  Chip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  Badge,
  LinearProgress,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox
} from '@mui/material';
import {
  Business,
  People,
  Security,
  AccountBalance,
  AttachMoney,
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Settings,
  Store,
  Person,
  AdminPanelSettings,
  BusinessCenter,
  MonetizationOn,
  CheckCircle,
  Warning,
  Info,
  Search,
  FilterList,
  Refresh,
  Save,
  Cancel,
  Clear,
  ExpandMore,
  CardMembership,
  Description,
  TrendingUp,
  Upload,
} from '@mui/icons-material';
import AppTheme from '../../shared-theme/AppTheme';
import SideMenu from '../../components/SideMenu';
import Header from '../../components/Header';
import Container from '@mui/material/Container';
import { alpha } from '@mui/material/styles';
import AlertMessageDialog from '../../components/Dialogs/alertMessageDialog';
import Can from '../../components/Can';
import { listaModulos, tiposCompania } from '../../utils/staticvariables';
import Selector from '../../components/Selector';
import { formatPhone } from '../../utils/generalFunctions';
import SubscriptionTab from './components/SubscriptionTab';
import ScoringTab from './components/ScoringTab';
import PlantillasIndex from './plantillas';
import ImportarPrestamosTab from './components/ImportarPrestamosTab';


const mockCurrencies = [
  { code: 'DOP', name: 'Peso Dominicano', symbol: 'RD$', rate: 1.0, isDefault: true },
  { code: 'USD', name: 'Dólar Estadounidense', symbol: '$', rate: 0.018, isDefault: false },
];

const mockPermissions = {
  "ventas": ["ver", "crear", "editar", "cancelar"],
  "prestamos": ["crear", "ver", "editar", "cancelar"],
  "inversionistas": ["ver", "crear", "editar", "cancelar"],
  "pagos": ["ver", "crear", "editar", "cancelar"],
  "iniciales": ["ver", "crear", "editar", "cancelar"],
  "categorias_cargos": ["ver", "crear", "editar", "eliminar"],
  "recuperaciones": ["ver", "crear", "editar", "cancelar"],
  "traspaso": ["ver", "crear", "editar", "cancelar"],
  "portafolio": ["ver", "crear", "editar", "cancelar"],
  "garantias": ["ver", "crear", "editar", "eliminar"],
  "categorias_inventario": ["ver", "crear", "editar", "eliminar"],
  "productos": ["ver", "crear", "editar", "eliminar"],
  "productos_financieros": ["ver", "crear", "editar", "eliminar"],
  "proveedores": ["ver", "crear", "editar", "eliminar"],
  "almacenes": ["ver", "crear", "editar", "eliminar"],
  "ordenes_compra": ["ver", "crear", "editar", "eliminar"],
  "lista_precios": ["ver", "crear", "editar", "eliminar"],
  "sucursales": ["ver", "crear", "editar", "eliminar"],
  "roles": ["ver", "crear", "editar", "eliminar"],
  "usuarios": ["ver", "crear", "editar", "eliminar"],
  "empresa": ["ver", "crear", "editar", "eliminar"],
  "reportes": ["ver", "exportar"],
  "bancos": ["ver", "crear", "editar", "eliminar"],
  "monedas": ["ver", "crear", "editar", "eliminar"],
  "subscriptions": ["ver", "crear", "editar", "cancelar"]
};
const SettingSection = ({ label, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        py: 1.25,
        color: 'primary.main',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        fontSize: '0.72rem',
        textAlign: 'left',
      }}
    >
      {label}
    </Typography>
    <Divider />
    {children}
  </Box>
);

const SettingRow = ({ label, description, children, last }) => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, gap: 4 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body1" fontWeight={600} color="text.primary" textAlign="left">{label}</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} textAlign="left">{description}</Typography>
        )}
      </Box>
      <Box sx={{ width: 300, flexShrink: 0 }}>{children}</Box>
    </Box>
    {!last && <Divider />}
  </>
);

const SectionPageHeader = ({ icon: Icon, title, description, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, pb: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Icon sx={{ fontSize: 26, color: 'text.secondary' }} />
      <Box>
        <Typography variant="h5" fontWeight={700} textAlign="left">{title}</Typography>
        {description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} textAlign="left">{description}</Typography>}
      </Box>
    </Box>
    {action}
  </Box>
);

const NAV_SECTIONS = [
  {
    group: 'Organización',
    items: [
      { key: 'empresa',    label: 'Empresa',    icon: Business },
      { key: 'sucursales', label: 'Sucursales', icon: Store },
      { key: 'modulos',    label: 'Módulos',    icon: Settings },
    ],
  },
  {
    group: 'Usuarios & Roles',
    items: [
      { key: 'usuarios', label: 'Usuarios', icon: People },
      { key: 'roles',    label: 'Roles',    icon: Security },
    ],
  },
  {
    group: 'Finanzas',
    items: [
      { key: 'monedas',       label: 'Monedas',       icon: AttachMoney },
      { key: 'suscripciones', label: 'Suscripciones', icon: CardMembership },
      { key: 'scoring',       label: 'Scoring',       icon: TrendingUp },
    ],
  },
  {
    group: 'Herramientas',
    items: [
      { key: 'plantillas', label: 'Plantillas',         icon: Description },
      { key: 'importar',   label: 'Importar Préstamos', icon: Upload },
    ],
  },
];

const Configuracion = () => {
  const [selectedSection, setSelectedSection] = useState('sucursales');
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companyData, setCompanyData] = useState({
    name: '',
    type: [],
    address: '',
    phone: '',
    taxId: '',
    country: '',
    modules: []
  });
  const [currencies, setCurrencies] = useState(mockCurrencies);
  const [permissions, setPermissions] = useState(mockPermissions);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedPermissions, setSelectedPermissions] = useState({});
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ open: false, message: '', severity: 'success' });
  
  // Module selection states
  const [selectedModules, setSelectedModules] = useState({});
  const [initialModules, setInitialModules] = useState({});
  
  // Validation states
  const [validationErrors, setValidationErrors] = useState({});
  
  // Filter states
  const [userFilters, setUserFilters] = useState({
    search: '',
    role: '',
    branch: '',
    status: ''
  });
  const [showUserFilters, setShowUserFilters] = useState(false);

  const handleOpenDialog = (type, item = null) => {
    setDialogType(type);
    setEditingItem(item);
    
    // Initialize form data based on type
    if (type === 'user' && item) {
      // Map user data to form fields
      setFormData({
        name: item.name || '',
        usuario: item.email || '', // email field contains the username
        roleId: item.roleId || '',
        branchId: item.branchId || '',
        password: '' // Don't populate password for security
      });
    } else {
      setFormData(item || {});
    }
    
    // Initialize permissions for role dialog
    if (type === 'role') {
      if (item && item.permissions) {
        // If editing existing role, load its permissions
        const rolePermissions = {};
        Object.entries(item.permissions).forEach(([modulo, acciones]) => {
          acciones.forEach(accion => {
            rolePermissions[modulo + '_' + accion] = true;
          });
        });
        setSelectedPermissions(rolePermissions);

      } else {
        // If creating new role, start with empty permissions
        setSelectedPermissions({});
      }
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogType('');
    setEditingItem(null);
    setFormData({});
    clearValidationErrors();
    // Don't reset selectedPermissions here - let it be reset when opening a new dialog
  };

  const handlePermissionChange = (module, action, checked) => {
    const permissionKey = `${module}_${action}`;
    
    setSelectedPermissions(prev => ({
      ...prev,
      [permissionKey]: checked
    }));
  };

  const handleSelectAllPermissions = (module, checked) => {
    const modulePermissions = permissions[module] || [];
    const newPermissions = { ...selectedPermissions };
    
    modulePermissions.forEach(action => {
      const permissionKey = `${module}_${action}`;
      newPermissions[permissionKey] = checked;
    });
    
    setSelectedPermissions(newPermissions);
  };

  const handleSelectAllGlobal = (checked) => {
    const newPermissions = {};
    
    Object.entries(permissions).forEach(([module, actions]) => {
      actions.forEach(action => {
        const permissionKey = `${module}_${action}`;
        newPermissions[permissionKey] = checked;
      });
    });
    
    setSelectedPermissions(newPermissions);
  };

  const getSelectedPermissionsArray = () => {
    return Object.keys(selectedPermissions).filter(key => selectedPermissions[key]);
  };

  // Fetch functions
  const fetchBranches = async () => {
    try {
      const idcompany = localStorage.getItem('idcompany');
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/sucursales?idcompany=${idcompany}`,{
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });           
      
      const hasError = await handleApiError(response, 'obtener sucursales');
      if (!hasError && response.ok) {
        const data = await response.json();
        // Ensure data is an array before processing
        if (Array.isArray(data)) {
          // Transform the data to match our expected format
          const transformedBranches = data.map(branch => ({
            id: branch.value,
            name: branch.label,
            address: branch.direccion || '',
            phone: branch.telefono || '',
            manager: '', // This would need to be fetched separately or added to the API
            status: 'active', // Default status
            employees: branch.num_usuarios || 0, // This would need to be calculated or fetched separately
            num_almacenes: branch.num_almacenes || 0
          }));
          setBranches(transformedBranches);
        } else {
          console.error('Expected array but got:', typeof data, data);
          setBranches([]); // Set to empty array if data is not an array
        }
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setAlertDialog({ open: true, message: 'Error al cargar sucursales', severity: 'error' });
    }
  };

  const createBranch = async (branchData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/sucursales`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id')
        },
        body: JSON.stringify({
          idcompany: localStorage.getItem('idcompany'),
          nombre: branchData.name,
          direccion: branchData.address,
          telefono: branchData.phone,
          altitud: 0.0,
          longitud: 0.0
        })
      });

      const hasError = await handleApiError(response, 'crear sucursal');
      if (!hasError && response.ok) {
        const result = await response.json();
        
        // If user wants to create warehouse automatically
        if (branchData.createWarehouse) {
          try {
            await createWarehouseForBranch(result.id, branchData.name, branchData.address);
          } catch (warehouseError) {
            console.error('Error creating warehouse:', warehouseError);
            // Don't fail the branch creation if warehouse creation fails
            setAlertDialog({ 
              open: true, 
              message: 'Sucursal creada exitosamente, pero hubo un error al crear el almacén', 
              severity: 'warning' 
            });
          }
        } else {
          setAlertDialog({ open: true, message: 'Sucursal creada exitosamente', severity: 'success' });
        }
        
        fetchBranches(); // Refresh the list
        return result;
      } else if (!hasError) {
        throw new Error('Error creating branch');
      }
    } catch (error) {
      console.error('Error creating branch:', error);
      if (!error.message.includes('Acceso denegado')) {
        setAlertDialog({ open: true, message: 'Error al crear sucursal', severity: 'error' });
      }
      throw error;
    }
  };

  const createWarehouseForBranch = async (branchId, branchName, branchAddress) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/almacenes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id')
        },
        body: JSON.stringify({
          idcompany: localStorage.getItem('idcompany'),
          nombre: `${branchName} - al`,
          descripcion: `Almacén principal de la sucursal ${branchName}`,
          branchesSelected: [branchId], // Array of branch IDs
          usersSelected: [localStorage.getItem('id')],
          tipo: 'principal',
          estado: 'activo',
          direccion: branchAddress,
          created_by: localStorage.getItem('id')
        })
      });

      const hasError = await handleApiError(response, 'crear almacén');
      if (!hasError && response.ok) {
        const result = await response.json();
        setAlertDialog({ 
          open: true, 
          message: 'Sucursal y almacén creados exitosamente', 
          severity: 'success' 
        });
        return result;
      } else if (!hasError) {
        throw new Error('Error creating warehouse');
      }
    } catch (error) {
      console.error('Error creating warehouse:', error);
      throw error;
    }
  };

  const updateBranch = async (branchId, branchData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/sucursales/${branchId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          nombre: branchData.name,
          direccion: branchData.address,
          telefono: branchData.phone,
          altitud: 0.0,
          longitud: 0.0
        })
      });

      const hasError = await handleApiError(response, 'actualizar sucursal');
      if (!hasError && response.ok) {
        setAlertDialog({ open: true, message: 'Sucursal actualizada exitosamente', severity: 'success' });
        fetchBranches(); // Refresh the list
        return true;
      } else if (!hasError) {
        throw new Error('Error updating branch');
      }
    } catch (error) {
      console.error('Error updating branch:', error);
      if (!error.message.includes('Acceso denegado')) {
        setAlertDialog({ open: true, message: 'Error al actualizar sucursal', severity: 'error' });
      }
      throw error;
    }
  };

  const deleteBranch = async (branchId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/sucursales/${branchId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });

      const hasError = await handleApiError(response, 'eliminar sucursal');
      if (!hasError && response.ok) {
        setAlertDialog({ open: true, message: 'Sucursal eliminada exitosamente', severity: 'success' });
        fetchBranches(); // Refresh the list
        return true;
      } else if (!hasError) {
        throw new Error('Error deleting branch');
      }
    } catch (error) {
      console.error('Error deleting branch:', error);
      if (!error.message.includes('Acceso denegado')) {
        setAlertDialog({ open: true, message: 'Error al eliminar sucursal', severity: 'error' });
      }
      throw error;
    }
  };

  // User fetch functions
  const fetchUsers = async () => {
    try {
      const idcompany = localStorage.getItem('idcompany');
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/usuarios?idcompany=${idcompany}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      const hasError = await handleApiError(response, 'obtener usuarios');
      if (!hasError && response.ok) {
        const data = await response.json();
        // Ensure data is an array before processing
        if (Array.isArray(data)) {
          // Transform the data to match our expected format
          const transformedUsers = data.map(user => ({
            id: user.value,
            name: user.description || user.label,
            email: user.label, // Using usuario as email
            role: user.role, // Role name for display
            numUsuarios: user.num_usuarios, // Number of users in role
            roleId: user.idrole, // Role ID for form
            branch: user.sucursal || '', // Branch name for display
            branchId: user.idsucursal, // Branch ID for form
            status: user.estado === 'activo' ? 'active' : 'inactive',
            lastLogin: '2024-01-15' // This would need to be fetched separately
          }));
          setUsers(transformedUsers);
        } else {
          console.error('Expected array but got:', typeof data, data);
          setUsers([]); // Set to empty array if data is not an array
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setAlertDialog({ open: true, message: 'Error al cargar usuarios', severity: 'error' });
    }
  };


  const fetchCompany = async (companyId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/companias/${companyId}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      const hasError = await handleApiError(response, 'obtener empresa');
      if (!hasError && response.ok) {
        const data = await response.json();
        // Transform the data to match our expected format
        const transformedCompany = {
          id: data.id,
          name: data.nombre,
          taxId: data.Idfiscal,
          address: data.direccion,
          phone: data.telefono,
          country: data.pais,
          companyTypes: data.company_types || [],
          modules: data.modulos || [],
          createdAt: data.created_at
        };
        return transformedCompany;
      } else if (!hasError) {
        console.error('Error fetching company:', response.statusText);
        return null;
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      setAlertDialog({ open: true, message: 'Error al cargar empresa', severity: 'error' });
      return null;
    }
  };

  const fetchCurrentCompany = async () => {
    try {
      const idcompany = localStorage.getItem('idcompany');
      if (!idcompany) {
        console.error('No company ID found in localStorage');
        setAlertDialog({ open: true, message: 'ID de empresa no encontrado', severity: 'error' });
        return null;
      }
      
      const company = await fetchCompany(idcompany);
      if (company) {
        // Transform the company data to match the expected format for the form
        const transformedCompanyData = {
          name: company.name,
          type: company.companyTypes && company.companyTypes.length > 0 ? JSON.parse(company.companyTypes) : ['vehicular'],
          currency: 'DOP', // Default currency
          country: company.country || 'RD',
          address: company.address,
          phone: company.phone,
          taxId: company.taxId,
          established: company.createdAt ? new Date(company.createdAt).toISOString().split('T')[0] : '2020-01-01'
        };
        setCompanyData(transformedCompanyData);
        // Initialize modules from transformed company modules
        initializeModules(company.modules || []);
      }
      return company;
    } catch (error) {
      console.error('Error fetching current company:', error);
      setAlertDialog({ open: true, message: 'Error al cargar empresa actual', severity: 'error' });
      return null;
    }
  };

  const createCompany = async (companyData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/companias`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          nombre: companyData.name,
          Idfiscal: companyData.taxId,
          direccion: companyData.address,
          telefono: companyData.phone,
          pais: companyData.country,
          company_types: companyData.companyTypes || [],
          modulos: companyData.modules || []
        })
      });

      if (response.ok) {
        setAlertDialog({ open: true, message: 'Empresa creada exitosamente', severity: 'success' });
        fetchCurrentCompany(); // Refresh the current company
        return true;
      } else {
        throw new Error('Error creating company');
      }
    } catch (error) {
      console.error('Error creating company:', error);
      setAlertDialog({ open: true, message: 'Error al crear empresa', severity: 'error' });
      throw error;
    }
  };

  const updateCompany = async (companyId, companyData) => {
    const selectedModulesArray = Object.keys(selectedModules).filter(key => selectedModules[key]);
    try {
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/companias/${companyId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          },
          body: JSON.stringify({
            nombre: companyData.name,
            Idfiscal: companyData.taxId,
            direccion: companyData.address,
            telefono: companyData.phone,
            pais: companyData.country,
            company_types: companyData.companyTypes || [],
            modulos: selectedModulesArray || []
          })
        });
  
        const hasError = await handleApiError(response, 'actualizar empresa');
        if (!hasError && response.ok) {
          setAlertDialog({ open: true, message: 'Empresa actualizada exitosamente', severity: 'success' });
          fetchCurrentCompany(); // Refresh the current company
          return true;
        } else if (!hasError) {
          throw new Error('Error updating company');
        }
      } catch (error) {
        console.error('Error updating company:', error);
        if (!error.message.includes('Acceso denegado')) {
          setAlertDialog({ open: true, message: 'Error al actualizar empresa', severity: 'error' });
        }
        throw error;
      }
    
  };

  const deleteCompany = async (companyId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/companias/${companyId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });

      const hasError = await handleApiError(response, 'eliminar empresa');
      if (!hasError && response.ok) {
        setAlertDialog({ open: true, message: 'Empresa eliminada exitosamente', severity: 'success' });
        fetchCurrentCompany(); // Refresh the current company
        return true;
      } else if (!hasError) {
        throw new Error('Error deleting company');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      if (!error.message.includes('Acceso denegado')) {
        setAlertDialog({ open: true, message: 'Error al eliminar empresa', severity: 'error' });
      }
      throw error;
    }
  };

  // Roles fetch functions
  const fetchRoles = async () => {
    try {
      const idcompany = localStorage.getItem('idcompany');
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/roles?idcompany=${idcompany}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      const hasError = await handleApiError(response, 'obtener roles');
      if (!hasError && response.ok) {
        const data = await response.json();
        // Ensure data is an array before processing
        if (Array.isArray(data)) {
          // Transform the data to match our expected format
          const transformedRoles = data.map(role => ({
            id: role.value,
            name: role.label,
            description: role.descripcion,
            permissions: role.permisos ? JSON.parse(role.permisos) : {},
            users: role.num_usuarios, // This would need to be fetched separately
            deletable: true, // Default to true, could be determined by business logic
            createdAt: role.created_at
          }));
          setRoles(transformedRoles);
        } else {
          console.error('Expected array but got:', typeof data, data);
          setRoles([]); // Set to empty array if data is not an array
        }
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setAlertDialog({ open: true, message: 'Error al cargar roles', severity: 'error' });
    }
  };

  const createRole = async (roleData) => {
    try {
        const idcompany = localStorage.getItem('idcompany');
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/roles`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          },
          body: JSON.stringify({
            nombre: roleData.name,
            descripcion: roleData.description,
            permisos: roleData.permissions || {},
            idcompany: idcompany,
            created_by: localStorage.getItem('id')
          })
        });
  
        const hasError = await handleApiError(response, 'crear rol');
        if (!hasError && response.ok) {
          setAlertDialog({ open: true, message: 'Rol creado exitosamente', severity: 'success' });
          fetchRoles(); // Refresh the list
          return true;
        } else if (!hasError) {
          throw new Error('Error creating role');
        }
      } catch (error) {
        console.error('Error creating role:', error);
        if (!error.message.includes('Acceso denegado')) {
          setAlertDialog({ open: true, message: 'Error al crear rol', severity: 'error' });
        }
        throw error;
      }
  
  };

  const updateRole = async (roleId, roleData) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/roles/${roleId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Usuario-ID': localStorage.getItem('id'),
            'X-company-ID': localStorage.getItem('idcompany')
          },
          body: JSON.stringify({
            nombre: roleData.name,
            descripcion: roleData.description,
            permisos: roleData.permissions || {}
          })
        });
  
        const hasError = await handleApiError(response, 'actualizar rol');
        if (!hasError && response.ok) {
          setAlertDialog({ open: true, message: 'Rol actualizado exitosamente', severity: 'success' });
          fetchRoles(); // Refresh the list
          return true;
        } else if (!hasError) {
          throw new Error('Error updating role');
        }
      } catch (error) {
        console.error('Error updating role:', error);
        if (!error.message.includes('Acceso denegado')) {
          setAlertDialog({ open: true, message: 'Error al actualizar rol', severity: 'error' });
        }
        throw error;
      }
   
  };

  const deleteRole = async (roleId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });

      const hasError = await handleApiError(response, 'eliminar rol');
      if (!hasError && response.ok) {
        setAlertDialog({ open: true, message: 'Rol eliminado exitosamente', severity: 'success' });
        fetchRoles(); // Refresh the list
        return true;
      } else if (!hasError) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error deleting role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      if (!error.message.includes('Acceso denegado')) {
        setAlertDialog({ open: true, message: error.message || 'Error al eliminar rol', severity: 'error' });
      }
      throw error;
    }
  };

  // User CRUD functions
  const createUser = async (userData) => {
    try {
      const idcompany = localStorage.getItem('idcompany');
      const userId = localStorage.getItem('id');
      
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/usuarios`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': userId,
          'X-company-ID': idcompany
        },
        body: JSON.stringify({
          id: userData.id || `user_${Date.now()}`, // Generate ID if not provided
          nombre: userData.name,
          usuario: userData.usuario,
          password: userData.password || 'password123', // Default password
          idsucursal: userData.branchId,
          idrol: userData.roleId,
          idcompany: idcompany,
          created_at: new Date().toISOString(),
          created_by: userId
        })
      });

      const hasError = await handleApiError(response, 'crear usuario');
      if (!hasError && response.ok) {
        setAlertDialog({ open: true, message: 'Usuario creado exitosamente', severity: 'success' });
        fetchUsers(); // Refresh the list
        return true;
      } else if (!hasError) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error creating user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      if (!error.message.includes('Acceso denegado')) {
        setAlertDialog({ open: true, message: error.message || 'Error al crear usuario', severity: 'error' });
      }
      throw error;
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      const idcompany = localStorage.getItem('idcompany');
      const currentUserId = localStorage.getItem('id');
      
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/usuarios`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': currentUserId,
          'X-company-ID': idcompany
        },
        body: JSON.stringify({
          id: userId,
          nombre: userData.name,
          usuario: userData.usuario,
          idsucursal: userData.branchId,
          idrol: userData.roleId,
          idcompany: idcompany,
          created_at: new Date().toISOString(),
          created_by: currentUserId
        })
      });

      const hasError = await handleApiError(response, 'actualizar usuario');
      if (!hasError && response.ok) {
        setAlertDialog({ open: true, message: 'Usuario actualizado exitosamente', severity: 'success' });
        fetchUsers(); // Refresh the list
        return true;
      } else if (!hasError) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error updating user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      if (!error.message.includes('Acceso denegado')) {
        setAlertDialog({ open: true, message: error.message || 'Error al actualizar usuario', severity: 'error' });
      }
      throw error;
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/usuarios`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          id: userId
        })
      });

      const hasError = await handleApiError(response, 'eliminar usuario');
      if (!hasError && response.ok) {
        setAlertDialog({ open: true, message: 'Usuario eliminado exitosamente', severity: 'success' });
        fetchUsers(); // Refresh the list
        return true;
      } else if (!hasError) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error deleting user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      if (!error.message.includes('Acceso denegado')) {
        setAlertDialog({ open: true, message: error.message || 'Error al eliminar usuario', severity: 'error' });
      }
      throw error;
    }
  };

  // Module selection handlers
  const handleModuleToggle = (moduleKey) => {
    setSelectedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  const handleSaveModules = async () => {
    setLoading(true);
    try {
      const idcompany = localStorage.getItem('idcompany');
      if (!idcompany) {
        setAlertDialog({ 
          open: true, 
          message: 'ID de empresa no encontrado', 
          severity: 'error' 
        });
        setLoading(false);
        return;
      }

      // Get current company data
      const currentCompany = await fetchCompany(idcompany);
      if (!currentCompany) {
        setAlertDialog({ 
          open: true, 
          message: 'Error al obtener datos de la empresa', 
          severity: 'error' 
        });
        setLoading(false);
        return;
      }

      // Convert selected modules to array format
      const selectedModulesArray = Object.keys(selectedModules).filter(key => selectedModules[key]);


      // Prepare data for update
      const updateData = {
        nombre: currentCompany.name,
        Idfiscal: currentCompany.taxId,
        direccion: currentCompany.address,
        telefono: currentCompany.phone,
        pais: currentCompany.country,
        company_types: currentCompany.companyTypes || [],
        modulos: selectedModulesArray
      };

      // Call the editar_compania API
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/companias/${idcompany}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        // Update initial state to current state after successful save
        setInitialModules({ ...selectedModules });
        
        setAlertDialog({ 
          open: true, 
          message: 'Módulos guardados exitosamente', 
          severity: 'success' 
        });
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar módulos');
      }
    } catch (error) {
      console.error('Error saving modules:', error);
      setAlertDialog({ 
        open: true, 
        message: error.message || 'Error al guardar módulos', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if there are changes in module selection
  const hasModuleChanges = () => {
    const currentKeys = Object.keys(selectedModules).filter(key => selectedModules[key]);
    const initialKeys = Object.keys(initialModules).filter(key => initialModules[key]);
    
    // Check if arrays have different lengths
    if (currentKeys.length !== initialKeys.length) {
      return true;
    }
    
    // Check if all selected modules are the same
    return !currentKeys.every(key => initialModules[key]);
  };

  // Initialize modules state (you can call this when loading existing company data)
  const initializeModules = (companyModules = []) => {
    const initialModuleState = {};
    listaModulos.forEach(group => {
      group.modulos.forEach(mod => {
        initialModuleState[mod.key] = companyModules.includes(mod.key);
      });
    });
    
    localStorage.setItem('modulos', JSON.stringify(companyModules));
    
    setSelectedModules(initialModuleState);
    setInitialModules(initialModuleState);
  };

  // Validation functions
  const validateBranchForm = (data) => {
    const errors = {};
    
    if (!data.name || data.name.trim() === '') {
      errors.name = 'El nombre de la sucursal es requerido';
    }
    
    if (!data.address || data.address.trim() === '') {
      errors.address = 'La dirección es requerida';
    }
    
    if (!data.phone || data.phone.trim() === '') {
      errors.phone = 'El teléfono es requerido';
    } else if (!/^[\d\s\-\+\(\)]+$/.test(data.phone)) {
      errors.phone = 'El teléfono debe contener solo números y caracteres válidos';
    }
    
    return errors;
  };

  const validateUserForm = (data) => {
    const errors = {};
    
    if (!data.name || data.name.trim() === '') {
      errors.name = 'El nombre completo es requerido';
    }
    
    if (!data.usuario || data.usuario.trim() === '') {
      errors.usuario = 'El nombre de usuario es requerido';
    } else if (data.usuario.length < 3) {
      errors.usuario = 'El nombre de usuario debe tener al menos 3 caracteres';
    }
    
    if (!data.roleId || data.roleId === '') {
      errors.roleId = 'Debe seleccionar un rol';
    }
    
    if (!data.branchId || data.branchId === '') {
      errors.branchId = 'Debe seleccionar una sucursal';
    }
    
    if (!editingItem && (!data.password || data.password.trim() === '')) {
      errors.password = 'La contraseña es requerida';
    } else if (!editingItem && data.password && data.password.length < 6) {
      errors.password = 'La contraseña debe tener al menos 6 caracteres';
    }
    
    return errors;
  };

  const clearValidationErrors = () => {
    setValidationErrors({});
  };

  // Centralized error handler for API responses
  const handleApiError = async (response, operation = 'operación') => {
    if (response.status === 403) {
      const errorData = await response.json();
      setAlertDialog({
        open: true,
        message: `Acceso denegado: No tienes permisos para realizar esta ${operation}. ${errorData.error || ''}`,
        severity: 'error'
      });
      return true; // Indicates permission error
    } else if (!response.ok) {
      const errorData = await response.json();
      setAlertDialog({
        open: true,
        message: `Error en ${operation}: ${errorData.error || 'Error desconocido'}`,
        severity: 'error'
      });
      return false; // Indicates other error
    }
    return false; // No error
  };

  // useEffect to fetch data on component mount
  useEffect(() => {
    fetchBranches();
    fetchUsers();
    fetchCurrentCompany();
    fetchRoles();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    clearValidationErrors();
    
    try {
      if (dialogType === 'branch') {
        // Validate branch form
        const branchErrors = validateBranchForm(formData);
        if (Object.keys(branchErrors).length > 0) {
          setValidationErrors(branchErrors);
          setLoading(false);
          return;
        }
        
        if (editingItem) {
          // Update existing branch
          await updateBranch(editingItem.id, formData);
        } else {
          // Create new branch
          await createBranch(formData);
        }
      } else if (dialogType === 'user') {
        // Validate user form
        const userErrors = validateUserForm(formData);
        if (Object.keys(userErrors).length > 0) {
          setValidationErrors(userErrors);
          setLoading(false);
          return;
        }
        
        if (editingItem) {
          // Update existing user
          await updateUser(editingItem.id, formData);
        } else {
          // Create new user
          await createUser(formData);
        }
      } else if (dialogType === 'company') {
        if (editingItem) {
          // Update existing company
          await updateCompany(editingItem.id, formData);
        } else {
          // Create new company
          await createCompany(formData);
        }
      } else if (dialogType === 'role') {
        // Transform selectedPermissions to the format expected by the API
        const transformedPermissions = {};
        Object.entries(selectedPermissions).forEach(([key, value]) => {
          if (value) {
            const [module,submodule, action] = key.split('_');
            if (!transformedPermissions[action?module+"_"+submodule:module]) {
              transformedPermissions[action?module+"_"+submodule:module] = [];
            }
            transformedPermissions[action?module+"_"+submodule:module].push(action?action:submodule);
          }
        });

        const roleData = {
          ...formData,
          permissions: transformedPermissions
        };

        if (editingItem) {
          // Update existing role
          await updateRole(editingItem.id, roleData);
        } else {
          // Create new role
          await createRole(roleData);
        }
      } else if (dialogType === 'user') {
        if (editingItem) {
          // Update existing user
          await updateUser(editingItem.id, formData);
        } else {
          // Create new user
          await createUser(formData);
        }
      } else {
        // For other types, simulate API call
        setTimeout(() => {
          setLoading(false);
          setAlertDialog({ open: true, message: 'Configuración guardada exitosamente', severity: 'success' });
          handleCloseDialog();
        }, 1000);
        return;
      }
      setLoading(false);
      handleCloseDialog();
    } catch (error) {
      setLoading(false);
      // Error handling is done in the individual functions
    }
  };

  const handleSaveCompanyData = async () => {
    setLoading(true);
    try {
      const idcompany = localStorage.getItem('idcompany');
      if (!idcompany) {
        setAlertDialog({ open: true, message: 'ID de empresa no encontrado', severity: 'error' });
        setLoading(false);
        return;
      }

      // Transform the form data to match the API format
      const apiData = {
        name: companyData.name,
        taxId: companyData.taxId,
        address: companyData.address,
        phone: companyData.phone,
        country: companyData.country,
        companyTypes: [companyData.type], // Convert single type to array
        modules: [] // Default empty modules
      };

      await updateCompany(idcompany, apiData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // Error handling is done in the updateCompany function
    }
  };
  const handleDelete = async (type, id) => {
    if (type === 'branch') {
      try {
        await deleteBranch(id);
      } catch (error) {
        // Error handling is done in the deleteBranch function
      }
    } else if (type === 'company') {
      try {
        await deleteCompany(id);
      } catch (error) {
        // Error handling is done in the deleteCompany function
      }
    } else if (type === 'role') {
      try {
        await deleteRole(id);
      } catch (error) {
        // Error handling is done in the deleteRole function
      }
    } else if (type === 'user') {
      try {
        await deleteUser(id);
      } catch (error) {
        // Error handling is done in the deleteUser function
      }
    } else {
      // Handle delete logic for other types
      setAlertDialog({ open: true, message: 'Elemento eliminado exitosamente', severity: 'success' });
    }
  };

  const handleUserFilterChange = (field, value) => {
    setUserFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearUserFilters = () => {
    setUserFilters({
      search: '',
      role: '',
      branch: '',
      status: ''
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !userFilters.search || 
      user.name.toLowerCase().includes(userFilters.search.toLowerCase()) ||
      user.email.toLowerCase().includes(userFilters.search.toLowerCase());
    
    const matchesRole = !userFilters.role || user.role === userFilters.role;
    const matchesBranch = !userFilters.branch || user.branch === userFilters.branch;
    const matchesStatus = !userFilters.status || user.status === userFilters.status;
    
    return matchesSearch && matchesRole && matchesBranch && matchesStatus;
  });

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'error';
  };

  const getStatusIcon = (status) => {
    return status === 'active' ? <CheckCircle /> : <Warning />;
  };

  const renderBranchesTab = () => (
    <Box>
      <SectionPageHeader
        icon={Store}
        title="Sucursales"
        action={
          <Can modulo="sucursales" accion="crear">
            <Button variant="contained" color="primarybutton" startIcon={<Add />} onClick={() => handleOpenDialog('branch')} sx={{ fontWeight: 'bold' }}>
              Nueva Sucursal
            </Button>
          </Can>
        }
      />
      <Box>
        {branches.map((branch, index) => (
          <React.Fragment key={branch.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 2, gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', flexShrink: 0 }}><Store /></Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" fontWeight={600}>{branch.name}</Typography>
                  <Chip icon={getStatusIcon(branch.status)} label={branch.status === 'active' ? 'Activa' : 'Inactiva'} color={getStatusColor(branch.status)} size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {[branch.address, branch.phone, `${branch.employees} empleados`, `${branch.num_almacenes} almacenes`].filter(Boolean).join(' • ')}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexShrink={0}>
                <Can modulo="sucursales" accion="editar">
                  <Button variant="outlined" size="small" onClick={() => handleOpenDialog('branch', branch)} sx={{ fontWeight: 'bold' }}>Editar</Button>
                </Can>
                <Can modulo="sucursales" accion="eliminar">
                  <Button variant="outlined" color="error" size="small" onClick={() => handleDelete('branch', branch.id)} sx={{ fontWeight: 'bold' }}>Eliminar</Button>
                </Can>
              </Stack>
            </Box>
            {index < branches.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );

  const renderUsersTab = () => (
    <Box>
      <SectionPageHeader
        icon={People}
        title="Usuarios"
        action={
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" startIcon={<FilterList />} onClick={() => setShowUserFilters(!showUserFilters)} sx={{ fontWeight: 'bold' }}>
              Filtros
            </Button>
            <Can modulo="usuarios" accion="crear">
              <Button variant="contained" color="primarybutton" startIcon={<Add />} onClick={() => handleOpenDialog('user')} sx={{ fontWeight: 'bold' }}>
                Nuevo Usuario
              </Button>
            </Can>
          </Stack>
        }
      />

      {showUserFilters && (
        <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>Filtros</Typography>
            <Typography variant="caption" color="text.secondary">{filteredUsers.length} de {users.length} usuarios</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth size="small" placeholder="Buscar por nombre o email..." value={userFilters.search} onChange={(e) => handleUserFilterChange('search', e.target.value)} />
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <Select value={userFilters.role} onChange={(e) => handleUserFilterChange('role', e.target.value)} displayEmpty>
                  <MenuItem value="">Todos los roles</MenuItem>
                  {roles.map((role) => <MenuItem key={role.id} value={role.name}>{role.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <Select value={userFilters.branch} onChange={(e) => handleUserFilterChange('branch', e.target.value)} displayEmpty>
                  <MenuItem value="">Todas las sucursales</MenuItem>
                  {branches.map((branch) => <MenuItem key={branch.id} value={branch.name}>{branch.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <Select value={userFilters.status} onChange={(e) => handleUserFilterChange('status', e.target.value)} displayEmpty>
                  <MenuItem value="">Todos los estados</MenuItem>
                  <MenuItem value="active">Activo</MenuItem>
                  <MenuItem value="inactive">Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {Object.values(userFilters).some(v => v) && (
            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" startIcon={<Clear />} onClick={clearUserFilters} sx={{ color: 'error.main' }}>Limpiar filtros</Button>
            </Box>
          )}
        </Box>
      )}

      <Box>
        {filteredUsers.map((user, index) => (
          <React.Fragment key={user.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 2, gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', flexShrink: 0 }}><Person /></Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" fontWeight={600}>{user.name}</Typography>
                  <Chip icon={getStatusIcon(user.status)} label={user.status === 'active' ? 'Activo' : 'Inactivo'} color={getStatusColor(user.status)} size="small" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {[user.email, user.role, user.branch].filter(Boolean).join(' • ')}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} flexShrink={0}>
                <Can modulo="usuarios" accion="editar">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleOpenDialog('user', user)}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                </Can>
                <Can modulo="usuarios" accion="eliminar">
                  <Tooltip title="Eliminar">
                    <IconButton size="small" color="error" onClick={() => handleDelete('user', user.id)}><Delete fontSize="small" /></IconButton>
                  </Tooltip>
                </Can>
              </Stack>
            </Box>
            {index < filteredUsers.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );

  const renderRolesTab = () => (
    <Box>
      <SectionPageHeader
        icon={Security}
        title="Roles"
        action={
          <Can modulo="roles" accion="crear">
            <Button variant="contained" color="primarybutton" startIcon={<Add />} onClick={() => handleOpenDialog('role')} sx={{ fontWeight: 'bold' }}>
              Nuevo Rol
            </Button>
          </Can>
        }
      />
      <Box>
        {roles.map((role, index) => (
          <React.Fragment key={role.id}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 2.5, gap: 2 }}>
              <Avatar sx={{ bgcolor: 'secondary.main', flexShrink: 0 }}><AdminPanelSettings /></Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body1" fontWeight={600}>{role.name}</Typography>
                  <Typography variant="caption" color="text.secondary">({role.users} usuario(s))</Typography>
                </Box>
                {role.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{role.description}</Typography>
                )}
                <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
                  {Object.keys(role.permissions).map((modulo) => (
                    <Chip key={modulo} label={modulo} size="small" variant="outlined" color="primary" />
                  ))}
                </Stack>
              </Box>
              <Stack direction="row" spacing={1} flexShrink={0}>
                <Can modulo="roles" accion="editar">
                  <Button variant="outlined" size="small" onClick={() => handleOpenDialog('role', role)} sx={{ fontWeight: 'bold' }}>Editar</Button>
                </Can>
                <Can modulo="roles" accion="eliminar">
                  <Button variant="outlined" color="error" size="small" onClick={() => handleDelete('role', role.id)} sx={{ fontWeight: 'bold' }}>Eliminar</Button>
                </Can>
              </Stack>
            </Box>
            {index < roles.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );

  const renderModulesTab = () => (
    <Box>
      <SectionPageHeader
        icon={Settings}
        title="Módulos"
        description="Selecciona los módulos activos del sistema"
        action={
          <Button variant="contained" startIcon={<Save />} onClick={handleSaveModules} disabled={!hasModuleChanges() || loading} color="primarybutton" sx={{ fontWeight: 'bold' }}>
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        }
      />
      {listaModulos.map((grupo) => (
        <SettingSection key={grupo.categoria} label={grupo.categoria}>
          {grupo.modulos.map((mod, idx) => (
            <React.Fragment key={mod.key}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.75 }}>
                <Box>
                  <Typography variant="body1" fontWeight={500}>{mod.label}</Typography>
                  {mod.default && <Typography variant="caption" color="primary.main" fontWeight={600}>Recomendado</Typography>}
                </Box>
                <Checkbox disabled={mod.default} checked={!!selectedModules[mod.key]} onChange={() => handleModuleToggle(mod.key)} />
              </Box>
              {idx < grupo.modulos.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </SettingSection>
      ))}
    </Box>
  );

  const renderCompanyTab = () => (
    <Box>
      <SectionPageHeader icon={Business} title="Empresa" description="Información legal y de contacto de su empresa" />

      <SettingSection label="Información General">
        <SettingRow label="Nombre de la Empresa" description="Nombre legal registrado">
          <TextField fullWidth size="small" placeholder="Nombre de la Empresa" value={companyData?.name || ''} onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })} />
        </SettingRow>
        <SettingRow label="Tipo de Empresa" description="Categoría o sector de actividad">
          <FormControl fullWidth size="small">
            <Selector multiple value={Array.isArray(companyData?.type) ? companyData.type : []} onChange={(e) => setCompanyData({ ...companyData, type: e.target.value })} options={tiposCompania} placeholder="Tipo de Empresa" />
          </FormControl>
        </SettingRow>
        <SettingRow label="RNC / Tax ID" description="Número de registro fiscal" last>
          <TextField fullWidth size="small" placeholder="RNC/Tax ID" value={companyData?.taxId || ''} onChange={(e) => setCompanyData({ ...companyData, taxId: e.target.value })} />
        </SettingRow>
      </SettingSection>

      <SettingSection label="Contacto">
        <SettingRow label="Dirección" description="Dirección física de la empresa">
          <TextField fullWidth size="small" placeholder="Dirección" value={companyData?.address || ''} onChange={(e) => setCompanyData({ ...companyData, address: e.target.value })} />
        </SettingRow>
        <SettingRow label="Teléfono" description="Número de contacto principal" last>
          <TextField fullWidth size="small" placeholder="Teléfono" value={companyData?.phone || ''} onChange={(e) => setCompanyData({ ...companyData, phone: e.target.value })} />
        </SettingRow>
      </SettingSection>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" color="primarybutton" startIcon={<Save />} onClick={handleSaveCompanyData} disabled={loading} sx={{ fontWeight: 'bold' }}>
          Guardar Cambios
        </Button>
      </Box>
    </Box>
  );

  const renderCurrenciesTab = () => (
    <Box>
      <SectionPageHeader
        icon={AttachMoney}
        title="Monedas"
        action={
          <Can modulo="monedas" accion="crear">
            <Button variant="contained" color="primarybutton" startIcon={<Add />} onClick={() => handleOpenDialog('currency')} sx={{ fontWeight: 'bold' }}>
              Nueva Moneda
            </Button>
          </Can>
        }
      />
      <Box>
        {currencies.map((currency, index) => (
          <React.Fragment key={currency.code}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 2, gap: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main', flexShrink: 0 }}><MonetizationOn /></Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" fontWeight={600}>{currency.name}</Typography>
                  {currency.isDefault && <Chip label="Principal" color="primary" size="small" />}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Código: {currency.code} • Símbolo: {currency.symbol} • Tasa: {currency.rate}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} flexShrink={0}>
                <Can modulo="monedas" accion="editar">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleOpenDialog('currency', currency)}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                </Can>
                {!currency.isDefault && (
                  <Can modulo="monedas" accion="eliminar">
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => handleDelete('currency', currency.code)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </Can>
                )}
              </Stack>
            </Box>
            {index < currencies.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );

  const renderDialog = () => {
    const getDialogTitle = () => {
      switch (dialogType) {
        case 'branch': return editingItem ? 'Editar Sucursal' : 'Nueva Sucursal';
        case 'user': return editingItem ? 'Editar Usuario' : 'Nuevo Usuario';
        case 'role': return editingItem ? 'Editar Rol' : 'Nuevo Rol';
        case 'currency': return editingItem ? 'Editar Moneda' : 'Nueva Moneda';
        default: return 'Configuración';
      }
    };

    return (
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings sx={{color:"primary.main"}}/>
            <Typography variant="h6" fontWeight={600}>
              {getDialogTitle()}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {dialogType === 'branch' && (
                <>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign={"left"} sx={{ml:1}}>Nombre de la Sucursal</Typography>
                    <TextField
                      fullWidth
                      placeholder="Nombre de la Sucursal"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      error={!!validationErrors.name}
                      helperText={validationErrors.name}
                      required
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign={"left"} sx={{ml:1}}>Teléfono</Typography>
                    <TextField
                      fullWidth
                      placeholder="Teléfono"
                      value={formatPhone(formData.phone || '')}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      error={!!validationErrors.phone}
                      helperText={validationErrors.phone}
                      required
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign={"left"} sx={{ml:1}}>Dirección</Typography>
                    <TextField
                      fullWidth
                      placeholder="Dirección"
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      error={!!validationErrors.address}
                      helperText={validationErrors.address}
                      required
                    />
                  </Grid>
                  {!editingItem && (
                    <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.createWarehouse || false}
                            onChange={(e) => setFormData({ ...formData, createWarehouse: e.target.checked })}
                            sx={{
                              '&.Mui-checked': {
                                color: 'primary.main',
                              }
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" color="text.primary" fontWeight={500}>
                              Crear almacén automáticamente
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Se creará un almacén principal para esta sucursal con el mismo nombre
                            </Typography>
                          </Box>
                        }
                        sx={{
                          width: '100%',
                          margin: 0,
                          padding: 2,
                          borderRadius: 1,
                          backgroundColor: formData.createWarehouse ? 'primary.50' : 'transparent',
                          border: formData.createWarehouse ? '1px solid' : '1px solid transparent',
                          borderColor: formData.createWarehouse ? 'primary.main' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'primary.50',
                            borderColor: 'primary.main',
                          },
                          '& .MuiFormControlLabel-label': {
                            width: '100%',
                          }
                        }}
                      />
                    </Grid>
                  )}
                </>
              )}
              
              {dialogType === 'user' && (
                <>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign={"left"} sx={{ ml: 1 }}>Nombre Completo</Typography>
                    <TextField
                      fullWidth
                      placeholder="Nombre Completo"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      error={!!validationErrors.name}
                      helperText={validationErrors.name}
                      required
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign={"left"} sx={{ ml: 1 }}>Usuario</Typography>
                    <TextField
                      fullWidth
                      placeholder="Usuario"
                      value={formData.usuario || ''}
                      onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
                      type="text"
                      error={!!validationErrors.usuario}
                      helperText={validationErrors.usuario}
                      required
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign={"left"} sx={{ ml: 1 }}>
                      Rol
                    </Typography>
                    <FormControl fullWidth error={!!validationErrors.roleId}>
                      <Select
                        value={formData.roleId || ''}
                        onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
                        displayEmpty
                        sx={{
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 1
                          }
                        }}
                      >
                        <MenuItem value="" disabled>Selecciona un rol</MenuItem>
                        {roles.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            {role.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.roleId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {validationErrors.roleId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign={"left"} sx={{ ml: 1 }}>
                      Sucursal
                    </Typography>
                    <FormControl fullWidth error={!!validationErrors.branchId}>
                      <Select
                        value={formData.branchId || ''}
                        onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                        displayEmpty
                        sx={{
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: 1
                          }
                        }}
                      >
                        <MenuItem value="" disabled>Selecciona una sucursal</MenuItem>
                        {branches.map((branch) => (
                          <MenuItem key={branch.id} value={branch.id}>
                            {branch.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.branchId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {validationErrors.branchId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  {!editingItem && (
                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign={"left"} sx={{ ml: 1 }}>
                        Contraseña
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Contraseña"
                        value={formData.password || ''}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        type="password"
                        error={!!validationErrors.password}
                        helperText={validationErrors.password}
                        required
                      />
                    </Grid>
                  )}
                </>
              )}
              
              {dialogType === 'role' && (
                <>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign={"left"} sx={{ ml: 1 }}>Nombre del Rol</Typography>
                    <TextField
                      fullWidth
                      placeholder="Nombre del Rol"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <Typography variant="body2" fontWeight={600} color="text.secondary" textAlign={"left"} sx={{ ml: 1 }}>Descripción</Typography>
                    <TextField
                      fullWidth
                      placeholder="Descripción del rol"
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </Grid>
                  
                  {/* Permissions Section */}
                  <Grid item size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
                      <Typography variant="h6" fontWeight={600} color="text.primary">
                        Permisos del Rol
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSelectAllGlobal(true)}
                          sx={{ 
                            fontWeight: 'bold',
                            textTransform: 'none',
                            minWidth: 'auto',
                            px: 2
                          }}
                        >
                          Seleccionar Todo
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleSelectAllGlobal(false)}
                          sx={{ 
                            fontWeight: 'bold',
                            textTransform: 'none',
                            minWidth: 'auto',
                            px: 2
                          }}
                        >
                          Deseleccionar Todo
                        </Button>
                      </Box>
                    </Box>
                    <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                      {Object.entries(permissions).map(([module, actions]) => {
                        const modulePermissions = actions || [];
                        const allSelected = modulePermissions.every(action => 
                          selectedPermissions[`${module}_${action}`]
                        );
                        const someSelected = modulePermissions.some(action => 
                          selectedPermissions[`${module}_${action}`]
                        );
                        
                        return (
                          <Accordion key={module} sx={{ mb: 1, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                            <AccordionSummary
                              expandIcon={<ExpandMore />}
                              sx={{
                                backgroundColor: 'white',
                                '&:hover': { backgroundColor: '#f5f5f5' }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Checkbox
                                  checked={allSelected}
                                  indeterminate={someSelected && !allSelected}
                                  onChange={(e) => handleSelectAllPermissions(module, e.target.checked)}
                                  sx={{ p: 0.5 }}
                                />
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {module.charAt(0).toUpperCase() + module.slice(1).replace('_', ' ')}
                                </Typography>
                                <Chip
                                  label={`${modulePermissions.filter(action => selectedPermissions[`${module}_${action}`]).length}/${modulePermissions.length}`}
                                  size="small"
                                  color={allSelected ? 'success' : someSelected ? 'warning' : 'default'}
                                  variant="outlined"
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ backgroundColor: '#fafafa', pt: 0 }}>
                              <Grid container spacing={1}>
                                {modulePermissions.map((action) => {
                                  const permissionKey = `${module}_${action}`;
                                  const isSelected = selectedPermissions[permissionKey];
                                  
                                  return (
                                    <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={action}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={isSelected}
                                            onChange={(e) => handlePermissionChange(module, action, e.target.checked)}
                                            size="small"
                                            key={`${permissionKey}-${isSelected}`}
                                          />
                                        }
                                        label={
                                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                            {action}
                                          </Typography>
                                        }
                                        sx={{ 
                                          m: 0,
                                          '& .MuiFormControlLabel-label': {
                                            fontSize: '0.875rem'
                                          }
                                        }}
                                      />
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Paper>
                  </Grid>
                </>
              )}
              
              {dialogType === 'currency' && (
                <>
                <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Código de Moneda"
                      value={formData.code || ''}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Nombre"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Símbolo"
                      value={formData.symbol || ''}
                      onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Tasa de Cambio"
                      value={formData.rate || ''}
                      onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                      type="number"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} startIcon={<Cancel />} variant="outlined" color="inherit">
            Cancelar
          </Button>
          <Button
            sx={{fontWeight:"bold"}}
            color="primarybutton"
            onClick={handleSave}
            variant="contained"
            startIcon={<Save />}
            disabled={loading}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderSection = () => {
    switch (selectedSection) {
      case 'sucursales':    return renderBranchesTab();
      case 'usuarios':      return renderUsersTab();
      case 'roles':         return renderRolesTab();
      case 'modulos':       return renderModulesTab();
      case 'empresa':       return renderCompanyTab();
      case 'monedas':       return renderCurrenciesTab();
      case 'suscripciones': return <SubscriptionTab />;
      case 'scoring':       return <ScoringTab />;
      case 'plantillas':    return <PlantillasIndex />;
      case 'importar':      return <ImportarPrestamosTab />;
      default:              return renderBranchesTab();
    }
  };

  return (
    <AppTheme>
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{ alignItems: 'center', mx: 3, pb: 5, mt: { xs: 8, md: 5 } }}
          >
            <Container maxWidth={false} sx={{ px: 4, py: 3 }}>

         
              {/* Two-column layout */}
              <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>

                {/* Left settings nav */}
                <Box
                  sx={{
                    width: 210,
                    flexShrink: 0,
                    position: 'sticky',
                    top: 24,
                  }}
                >
                  {NAV_SECTIONS.map((section) => (
                    <Box key={section.group} sx={{ mb: 2.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          px: 1.5,
                          mb: 0.5,
                          color: 'text.disabled',
                          fontWeight: 700,
                          textAlign:'left',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          fontSize: '0.68rem',
                        }}
                      >
                        {section.group}
                      </Typography>
                      {section.items.map((item) => {
                        const active = selectedSection === item.key;
                        const Icon = item.icon;
                        return (
                          <ListItemButton
                            key={item.key}
                            selected={active}
                            onClick={() => setSelectedSection(item.key)}
                            sx={{
                              borderRadius: 1.5,
                              mb: 0.25,
                              px: 1.5,
                              py: 0.75,
                              gap: 1.25,
                              borderLeft: '3px solid',
                              borderLeftColor: active ? 'primary.main' : 'transparent',
                              '&.Mui-selected': { bgcolor: 'action.selected' },
                              '&.Mui-selected:hover': { bgcolor: 'action.selected' },
                            }}
                          >
                            <Icon
                              sx={{
                                fontSize: 18,
                                color: active ? 'primary.main' : 'text.secondary',
                                flexShrink: 0,
                              }}
                            />
                            <ListItemText
                              primary={item.label}
                              slotProps={{
                                primary: {
                                  sx: {
                                    fontSize: '0.85rem',
                                    fontWeight: active ? 600 : 400,
                                    color: active ? 'primary.main' : 'text.secondary',
                                  },
                                },
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </Box>
                  ))}
                </Box>

                {/* Right content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {renderSection()}
                </Box>
              </Box>

              {renderDialog()}
              <AlertMessageDialog
                open={alertDialog.open}
                message={alertDialog.message}
                severity={alertDialog.severity}
                handleClose={() => setAlertDialog({ ...alertDialog, open: false })}
              />

            </Container>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
};

export default Configuracion;
